import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Icon, Radio } from 'bsa-ui';
import { MdPrint } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DenormalizedHistoryItem } from '@modules/advancement/advancementHistory/utils';
import { Position, RosterScout } from '@modules/advancement/duck/types';
import { navigateToBulkBlueCard } from '@modules/bulkEntry/duck/actions';
import { useUnitPendingLeadershipPositions } from '@modules/leadershipPositions/hooks';
import { notificationCountsSel } from '@modules/userNotifications/duck/selectors';
import {
  Badge,
  activityTypeIds,
  advancementHistoryResponse,
  goToActivityLogs,
  goToAdultActivityLogs,
  goToAdultProfile,
  goToYouthProfile,
  offlineSel,
  pendingAdvancementsByTypeSel,
  shouldFilterUsersBySubunitsSel,
} from '@shared';
import {
  Button,
  ConditionalRender,
  S,
  SearchInput,
  Spin,
  radioGroupStyles,
} from '@shared/components';
import { isMobileSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import { currentUnitGuidSel } from '@unit/duck/selectors';

import {
  currentUserSubunitsSel,
  loadingSel,
} from '../../../packRoster/duck/selectors';
import { PendingItemsTabs, tabsToAdvancementTypeMap } from '../../constants';
import {
  filteredItemsSel,
  getPendingItemsTabsSel,
  pendingItemsActiveTabSel,
  searchFilterSel,
  selectedRowKeysSel,
  setPendingItemsActiveTab,
  setSearchFilter,
  setSelectedRowKeys,
  setSorter,
  sorterSel,
} from '../../duck';
import { useHistoryItemsQuery, usePendingActivitiesQuery } from '../../hooks';
import { NotApprovableItemsTable } from './NotApprovableItems/NotApprovableItemsTable';
import styles from './PendingItems.less';
import PendingItemsList from './PendingItemsList';
import { PendingRequirementsList } from './PendingItemsList/PendingRequirementsList';
import PendingItemsTable from './PendingItemsTable';
import { PendingLeadershipPositionsTable } from './PendingItemsTable/PendingLeadershipPositionsTable';
import { PendingRequirementsTable } from './PendingItemsTable/PendingRequirementsTable';

const antIcon = <Icon type="loading" spin style={{ fontSize: 12 }} />;

const RadioButton = Radio.Button;
const RadioGroup = Radio.Group;

const isUserInSubUnit = (
  item: DenormalizedHistoryItem & Pick<RosterScout, 'positions'>,
  mySubUnits: Position[] = [],
) =>
  item.positions?.some(pos =>
    mySubUnits.some(mySub => {
      const mySubId = mySub.patrolId || mySub.denId;
      const posSubId = pos.patrolId || pos.denId;
      return mySubId === posSubId;
    }),
  );

const PendingItems: React.FC<{
  isLoading: boolean;
}> = props => {
  const { isLoading } = props;
  const dispatch = useDispatch();
  const tabs = useSelector(getPendingItemsTabsSel);
  const activeTab = useSelector(pendingItemsActiveTabSel);

  const pendingByType = useSelector(pendingAdvancementsByTypeSel);
  const { pendingCount: pendingPositionsCount } =
    useUnitPendingLeadershipPositions();

  const isMobile = useSelector(isMobileSel);
  const isOffline = useSelector(offlineSel);
  const items = useSelector(filteredItemsSel);
  const search = useSelector(searchFilterSel);
  const sorter = useSelector(sorterSel);
  const notificationCounts = useSelector(notificationCountsSel);
  const selectedRowKeys = useSelector(selectedRowKeysSel);
  const shouldFilterUsersBySubunits = useSelector(
    shouldFilterUsersBySubunitsSel,
  );
  const unitGuid: string = useSelector(currentUnitGuidSel);

  const mySubUnits = useSelector(currentUserSubunitsSel);

  const { filteredItems } = useMemo(() => {
    if (!shouldFilterUsersBySubunits) {
      return { filteredItems: items, filteredPendingByType: pendingByType };
    }

    const filtered = items.map(item => ({
      ...item,
      isUserInSubUnit: isUserInSubUnit(item, mySubUnits),
    }));
    const filteredByType = Object.keys(pendingByType).reduce(
      (curr, categoryKey) => {
        const reduced = pendingByType[categoryKey].map(item => ({
          ...item,
          isUserInSubUnit: isUserInSubUnit(item, mySubUnits),
        }));
        return { ...curr, [categoryKey]: reduced };
      },
      {},
    );
    return { filteredItems: filtered, filteredPendingByType: filteredByType };
  }, [items, pendingByType, mySubUnits, shouldFilterUsersBySubunits]);

  const itemsByType = useMemo(
    () =>
      filteredItems.filter(item => {
        if (activeTab === PendingItemsTabs.ACTIVITIES)
          return (item.type as unknown as string) === 'activity';

        return item.advancementType === activeTab;
      }),
    [filteredItems, activeTab],
  );

  const [searchPinned, setSearchPinned] = useState(false);

  const handleSetSelectedRowKeys = useCallback(
    rowKeys => dispatch(setSelectedRowKeys(rowKeys)),
    [dispatch],
  );

  const handleGoToYouthProfile = useCallback(
    (userId, activityType) => (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!isOffline) {
        event.stopPropagation();
        if (
          activityType &&
          activityType !== activityTypeIds.EAGLE_SERVICE_PROJECT
        ) {
          dispatch(goToActivityLogs({ userId, activityType }));
        } else {
          dispatch(goToYouthProfile(userId));
        }
      }
    },
    [isOffline, dispatch],
  );

  const handleGoToAdultProfile = useCallback(
    (userId, activityType) => (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!isOffline) {
        event.stopPropagation();
        if (activityType) {
          dispatch(goToAdultActivityLogs({ userId, activityType }));
        } else {
          dispatch(goToAdultProfile(userId));
        }
      }
    },
    [isOffline, dispatch],
  );

  const handleSetSorter = useCallback(
    sorter => dispatch(setSorter(sorter)),
    [dispatch],
  );

  const handleSearch = useCallback(
    value => dispatch(setSearchFilter(value)),
    [dispatch],
  );

  const onClickBulkBlueCard = () =>
    dispatch(navigateToBulkBlueCard({ unitGuid }));

  let Component;

  if (activeTab === PendingItemsTabs.REQUIREMENTS) {
    Component = isMobile ? (
      <PendingRequirementsList
        onGoToYouthProfile={handleGoToYouthProfile}
        search={search}
      />
    ) : (
      <PendingRequirementsTable
        onGoToYouthProfile={handleGoToYouthProfile}
        onGoToAdultProfile={handleGoToAdultProfile}
      />
    );
  } else if (activeTab === PendingItemsTabs.LEADERSHIP_POSITIONS) {
    Component = <PendingLeadershipPositionsTable />;
  } else {
    Component = isMobile ? (
      <PendingItemsList
        items={itemsByType}
        loading={isLoading}
        isOffline={isOffline}
        selectedRowKeys={selectedRowKeys}
        searchPinned={searchPinned}
        onSetSelectedRowKeys={handleSetSelectedRowKeys}
        onGoToYouthProfile={handleGoToYouthProfile}
        onGoToAdultProfile={handleGoToAdultProfile}
      />
    ) : (
      <PendingItemsTable
        showFilter={false}
        items={itemsByType}
        loading={isLoading}
        isOffline={isOffline}
        sorter={sorter}
        selectedRowKeys={selectedRowKeys}
        onSetSelectedRowKeys={handleSetSelectedRowKeys}
        onGoToYouthProfile={handleGoToYouthProfile}
        onGoToAdultProfile={handleGoToAdultProfile}
        onSorterChange={handleSetSorter}
      />
    );
  }

  return (
    <React.Fragment>
      <div className={styles.tabsContainer}>
        <div className={styles.radioGroup}>
          <RadioGroup
            defaultValue={activeTab}
            size="large"
            className={radioGroupStyles.group}
            onChange={ev => {
              // reset selection
              dispatch(setSelectedRowKeys([]));
              dispatch(setPendingItemsActiveTab(ev.target.value));
            }}
          >
            {tabs.map(tabKey => {
              const isLeadershipTab =
                tabKey === PendingItemsTabs.LEADERSHIP_POSITIONS;

              let count = isLeadershipTab ? pendingPositionsCount : 0;

              switch (tabKey) {
                case PendingItemsTabs.REQUIREMENTS:
                  count = notificationCounts.requirementsCount;
                  break;
                case PendingItemsTabs.RANKS:
                  count = notificationCounts.ranksCount;
                  break;
                case PendingItemsTabs.ADVENTURES:
                  count = notificationCounts.adventuresCount;
                  break;
                case PendingItemsTabs.MERIT_BADGES:
                  count = notificationCounts.meritBadgesCount;
                  break;
                case PendingItemsTabs.AWARDS:
                  count = notificationCounts.awardsCount;
                  break;
                case PendingItemsTabs.LEADERSHIP_POSITIONS:
                  count = pendingPositionsCount;
                  break;
                default:
                  break;
              }

              const tabName = (
                <FormattedMessage
                  id={`advancement.PendingItems.tabs.${tabKey}`}
                />
              );
              return (
                <RadioButton
                  key={tabKey as string}
                  className={radioGroupStyles.radioBtn}
                  value={tabKey}
                >
                  {tabName}
                  {count > 0 && <Badge count={count} />}
                </RadioButton>
              );
            })}
          </RadioGroup>
          <ConditionalRender value={isLoading}>
            <div className={styles.loadingIndicator}>
              <Spin
                className={styles.loadingIndicatorWrapper}
                indicator={antIcon}
                size="small"
              />
              <S size="5" semibold>
                <FormattedMessage id="shared.loading" />
              </S>
            </div>
          </ConditionalRender>
        </div>
        <Button
          color="info"
          uppercase={false}
          icon={<MdPrint />}
          shape="round"
          onClick={onClickBulkBlueCard}
        >
          <FormattedMessage id="advancement.TabCard.bluecardBulkPrint" />
        </Button>
      </div>
      <SearchInput
        size="large"
        mobileSticky
        placeholder={intl.formatMessage({
          id: 'advancement.PendingItems.search',
        })}
        value={search}
        onChange={handleSearch}
        onPinChange={setSearchPinned}
      />
      {Component}
      <NotApprovableItemsTable isLoading={isLoading} />
    </React.Fragment>
  );
};

const PendingItemsContainer = () => {
  const dispatch = useDispatch();
  const rosterLoading = useSelector(loadingSel);
  const activeTab = useSelector(
    pendingItemsActiveTabSel,
  ) as keyof typeof tabsToAdvancementTypeMap;
  const advancementType = tabsToAdvancementTypeMap[activeTab];

  const {
    data: results,
    isFetching,
    isLoading,
  } = useHistoryItemsQuery({
    advancementType,
  });
  const { activities, isLoading: isLoadingActivities } =
    usePendingActivitiesQuery({
      skip: activeTab !== PendingItemsTabs.ACTIVITIES,
    });

  useEffect(
    () => () => {
      dispatch(setPendingItemsActiveTab(PendingItemsTabs.RANKS));
    },
    [dispatch],
  );

  // TODO: split activities into different component
  useEffect(() => {
    if (activeTab !== PendingItemsTabs.ACTIVITIES) return;
    dispatch(advancementHistoryResponse(activities));
  }, [activeTab, dispatch, activities]);

  useEffect(() => {
    if (activeTab === PendingItemsTabs.ACTIVITIES) return;
    dispatch(advancementHistoryResponse(results));
  }, [activeTab, dispatch, results]);

  return (
    <PendingItems
      isLoading={
        isFetching || rosterLoading || isLoading || isLoadingActivities
      }
    />
  );
};

export default PendingItemsContainer;
